import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

type AddressFieldValues = any;

/**
 * Custom hook for managing address field operations
 * Return functions to set/reset address/contact/port values
 */
export default function useNotifyPartyAndConsigneeAddressManager(partyType: string) {
  const { setValue } = useFormContext();

  /**
   * Sets address values from API response to form fields
   */
  const setAddressValues = useCallback(
    (address: AddressFieldValues) => {
      setValue(`schedule.${partyType}.address_name`, address.name);
      setValue(`schedule.${partyType}.address_street`, address.street_address);
      setValue(`schedule.${partyType}.address_street_line2`, address.street_address_line_two);
      setValue(`schedule.${partyType}.address_city`, address.city_name);
      setValue(`schedule.${partyType}.address_state`, address.state_name);
      setValue(`schedule.${partyType}.address_country`, address.country_name);
      setValue(`schedule.${partyType}.address_zipcode`, address.zipcode);
      setValue(`schedule.${partyType}.address_un_locode`, address.locode);
    },
    [partyType, setValue],
  );

  /**
   * Resets all address-related form fields
   */
  const resetAddressValues = useCallback(() => {
    const addressFields = [
      "address_reference_number",
      "address_name",
      "address_street",
      "address_street_line2",
      "address_city",
      "address_state",
      "address_country",
      "address_zipcode",
      "address_un_locode",
    ];
    addressFields.forEach((field) => setValue(`schedule.${partyType}.${field}`, ""));
  }, [partyType, setValue]);

  /**
   * Resets all contact-related form fields
   */
  const resetContactValues = useCallback(() => {
    const contactFields = ["contact_name", "contact_phone", "contact_email"];
    contactFields.forEach((field) => setValue(`schedule.${partyType}.${field}`, ""));
  }, [partyType, setValue]);

  /**
   * Resets all port-related form fields
   */
  const resetPortValues = useCallback(() => {
    const portFields = ["airport", "seaport", "airport_name", "port_name"];
    portFields.forEach((field) => setValue(`schedule.${partyType}.${field}`, ""));
  }, [partyType, setValue]);

  return {
    setAddressValues,
    resetAddressValues,
    resetContactValues,
    resetPortValues,
  };
}
